import type { KitTextLocale } from '../kitText';
import { storyPillText } from './storyCopy';
import { clamp01, outCubic } from './videoMotion';

/**
 * The end card of every long video (owner answers 2026-10-08 for the visualizer, the music video and
 * the lyric video; designs `asset-redesign/z2` and `z4`, `WideFrame`): the last 20 s from a downbeat.
 * The stack sits in the left column of the 1920 x 1080 frame (x 120 to 780): the cover in its theme
 * frame, 16 bars, the id line ("title · artist"), the pill by date ("Pre-save now", "Listen now",
 * "Coming soon") and the SmartLink, and the Free watermark under it. The right half (x 860 to 1800,
 * y 140 to 900) stays free for the YouTube end screen elements, and the Studio tells the artist which
 * end screen template to pick.
 *
 * One shared module for every long video: the server and the web Studio read the same numbers. Every
 * size is in px of the 1920 x 1080 frame. Pure.
 */

/** The end card covers the last 20 s, from a downbeat. */
export const LONG_VIDEO_END_CARD_SEC = 20;

/** The video safe box: clear of the YouTube title bar (y 0 to 120) and the player controls (y 920 on). */
export const LONG_VIDEO_SAFE_BOX = { x: 120, y: 140, w: 1680, h: 760 } as const;

/** The zone of the YouTube end screen elements: nothing of the kit goes there during the end card. */
export const LONG_VIDEO_END_SCREEN_ZONE = { x: 860, y: 140, w: 940, h: 760 } as const;

/** The size of the theme frame of the cover of each theme (WideFrame `geo`): the cover factor, the extra height, the record offset. */
export interface LongVideoCoverGeometry {
  /** The cover size factor (a sticker or a photo is smaller than the plain card). */
  coverFactor: number;
  /** The extra height of the frame (the Hype sticker shadow, the Handmade photo border). */
  extra: number;
  /** The Retro record: the cover moves left by this share of its width. */
  recordOffset: number;
  /** The height of the CTA group (the Handmade pill is taller). */
  ctaHeight: number;
}

const BASE_GEOMETRY: LongVideoCoverGeometry = { coverFactor: 1, extra: 0, recordOffset: 0, ctaHeight: 136 };

/** The cover geometry of a release theme (WideFrame). */
export function longVideoCoverGeometry(themeId: string): LongVideoCoverGeometry {
  switch (themeId) {
    case 'hype':
      return { ...BASE_GEOMETRY, coverFactor: 0.94, extra: 40 };
    case 'retro':
      return { ...BASE_GEOMETRY, coverFactor: 0.92, recordOffset: -0.17 };
    case 'handmade':
      return { ...BASE_GEOMETRY, coverFactor: 0.86, extra: 90, ctaHeight: 166 };
    default:
      return BASE_GEOMETRY;
  }
}

export interface LongVideoBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The places of the end card stack (the `endPose` of WideFrame). */
export interface LongVideoEndPose {
  /** The cover: `w` is the width of the picture; the frame of the theme sits around it. */
  cover: LongVideoBox;
  bars: LongVideoBox;
  /** The id line, small: the title 46 px and the artist 30 px times the theme factors. */
  id: LongVideoBox;
  /** The CTA group: the pill (88 px), the SmartLink line and the watermark. */
  cta: LongVideoBox;
}

/** The watermark row under the SmartLink line (the row lockup `longVideoEndCardLockup`, 16 px above it) adds this height to the CTA group. */
export const LONG_VIDEO_WATERMARK_ROW = 48;

/** The end card stack, vertically centred in the safe box, in the left column (x 120 to 780). Pure. */
export function longVideoEndPose(themeId: string, watermark: boolean): LongVideoEndPose {
  const geo = longVideoCoverGeometry(themeId);
  const ctaH = geo.ctaHeight + (watermark ? LONG_VIDEO_WATERMARK_ROW : 0);
  const rest = geo.extra + 24 + 64 + 22 + 56 + 26 + ctaH;
  // The cover gives up the room that the stack lacks (the Handmade photo with the Free watermark).
  const cw = Math.min(360 * geo.coverFactor, LONG_VIDEO_SAFE_BOX.h - rest);
  const total = cw + rest;
  const top = LONG_VIDEO_SAFE_BOX.y + (LONG_VIDEO_SAFE_BOX.h - total) / 2;
  const y = top + cw + geo.extra;
  return {
    cover: { x: 450 - cw / 2 + geo.recordOffset * cw, y: top + geo.extra / 2, w: cw, h: cw },
    bars: { x: 270, y: y + 24, w: 360, h: 64 },
    id: { x: 120, y: y + 24 + 64 + 22, w: 660, h: 56 },
    cta: { x: 120, y: y + 24 + 64 + 22 + 56 + 26, w: 660, h: ctaH },
  };
}

/**
 * The start of the end card: the first downbeat at or after `lengthSec - 20` (in video time); with
 * no downbeat there, `lengthSec - 20`. Null for a video too short for an end card (under 40 s). Pure.
 */
export function longVideoEndCardStartSec(lengthSec: number, downbeats: readonly number[]): number | null {
  if (lengthSec < 2 * LONG_VIDEO_END_CARD_SEC) {
    return null;
  }
  const wanted = lengthSec - LONG_VIDEO_END_CARD_SEC;
  const downbeat = downbeats.find((time) => time >= wanted - 0.01 && time < lengthSec - 1);
  return Math.round((downbeat ?? wanted) * 1000) / 1000;
}

/** The texts of the end card. */
export interface LongVideoEndCardText {
  title: string;
  artist: string;
  /** The pill by date ("Pre-save now", "Listen now", "Coming soon"). */
  cta: string;
  /** The SmartLink address with no scheme; null: none published (no line). */
  link: string | null;
}

/** The texts of the end card from the song and the date: the pill of a lyric Story (owner answers). Pure. */
export function longVideoEndCardText(input: {
  title: string;
  artist: string;
  releaseDate: string | null;
  today: string;
  link: string | null;
  presave?: boolean;
  /** The language of the words; absent: English. */
  locale?: KitTextLocale | null;
}): LongVideoEndCardText {
  return {
    title: input.title,
    artist: input.artist,
    cta: storyPillText('lyric', input.releaseDate, input.today, input.presave, input.locale),
    link: input.link,
  };
}

/**
 * The move into the end card over one bar from its start (`endP` of WideFrame): 0 before, 1 after.
 * The footage blurs (14 px) and darkens (55 %) with it; the CTA comes in on the last 40 % of the bar.
 */
export function longVideoEndProgress(
  t: number,
  startSec: number | null,
  barSec: number
): {
  move: number;
  blurPx: number;
  brightness: number;
  cta: number;
} {
  if (startSec === null) {
    return { move: 0, blurPx: 0, brightness: 1, cta: 0 };
  }
  const move = clamp01((t - startSec) / barSec);
  return {
    move,
    blurPx: Math.round(14 * move * 100) / 100,
    brightness: Math.round((1 - 0.55 * move) * 1000) / 1000,
    cta: outCubic(clamp01((t - startSec - barSec * 0.6) / (barSec * 0.4))),
  };
}
